"use client";

import { useLayoutEffect, useRef } from "react";

import type {
  SidebarDefinition,
  SidebarRegistration,
} from "@/types/sidebar-session";

import { useRightSidebarStore } from "@/stores/right-sidebar-store";

/** Metadata updates and owner lifetime are deliberately different effects. */
export function useSidebarRegistration<I, P>(
  definition: SidebarDefinition<I, P>,
  enabled = true,
): SidebarRegistration | null {
  const scopeToken = useRightSidebarStore((state) => state.scopeToken);
  const owner = useRef(Symbol("SIDEBAR_REGISTRATION"));
  const latest = useRef(definition);
  latest.current = definition;
  const registration = useRef<SidebarRegistration | null>(null);

  useLayoutEffect(() => {
    if (!enabled) return;
    const handle = useRightSidebarStore
      .getState()
      .register(latest.current, owner.current);
    registration.current = handle;
    return () => {
      useRightSidebarStore.getState().unregister(handle);
      if (registration.current === handle) registration.current = null;
    };
  }, [definition.id, enabled, scopeToken]);

  useLayoutEffect(() => {
    if (!enabled) return;
    useRightSidebarStore.getState().register(definition, owner.current);
  }, [definition, enabled, scopeToken]);

  return registration.current;
}
